<script lang="ts">
import type { SliderRootEmits, SliderRootProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'

export interface SliderProps extends SliderRootProps {
  class?: HTMLAttributes['class']
  trackClass?: HTMLAttributes['class']
  rangeClass?: HTMLAttributes['class']
  color?: 'primary' | 'accent' | (string & {})
}
</script>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import { SliderRange, SliderRoot, SliderThumb, SliderTrack, useForwardPropsEmits } from 'reka-ui'
import { computed, useAttrs } from 'vue'
import { cn } from '#ui-kit/lib/utils'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<SliderProps>()
const emits = defineEmits<SliderRootEmits>()
const attrs = useAttrs()

const delegatedProps = reactiveOmit(props, 'class', 'trackClass', 'rangeClass', 'color')
const forwarded = useForwardPropsEmits(delegatedProps, emits)

const rangeColorClass = computed(() => {
  if (props.disabled) return ''
  if (!props.color || props.color === 'accent') return 'bg-accent'
  if (props.color === 'primary') return 'bg-primary-900'
  if (props.color.startsWith('#')) return ''
  return props.color
})

const rangeColorStyle = computed(() =>
  !props.disabled && props.color?.startsWith('#') ? { backgroundColor: props.color } : undefined,
)

const thumbCount = computed(() => {
  const value = props.modelValue ?? props.defaultValue
  return Array.isArray(value) ? Math.max(value.length, 1) : 1
})

function rootAttrs() {
  if (thumbCount.value > 1) return { ...attrs, role: 'group' }
  const { 'aria-label': _label, 'aria-labelledby': _labelledby, ...rest } = attrs
  return rest
}
</script>

<template>
  <SliderRoot
    v-bind="{ ...rootAttrs(), ...forwarded }"
    :class="
      cn(
        'group relative flex shrink items-center select-none touch-none h-5 w-full min-w-8',
        'data-[orientation=vertical]:flex-col data-[orientation=vertical]:w-2 data-[orientation=vertical]:h-full',
        props.class,
      )
    "
  >
    <SliderTrack
      :class="
        cn(
          'bg-neutral-600 relative grow rounded-full h-2 cursor-pointer group-data-disabled:cursor-not-allowed data-[orientation=vertical]:h-full data-[orientation=vertical]:w-2',
          trackClass,
        )
      "
    >
      <SliderRange
        :class="
          cn(
            'absolute rounded-full h-full data-[orientation=vertical]:w-full group-data-disabled:bg-transparent',
            rangeColorClass,
            rangeClass,
          )
        "
        :style="rangeColorStyle"
      />
    </SliderTrack>
    <SliderThumb
      v-for="key in thumbCount"
      :key="key"
      :aria-label="thumbCount === 1 ? attrs['aria-label'] : undefined"
      :aria-labelledby="thumbCount === 1 ? attrs['aria-labelledby'] : undefined"
      class="block w-5 h-5 bg-light ring-1 ring-inset ring-neutral-600 rounded-full focus:outline-hidden cursor-pointer focus:ring-2 focus:ring-primary-500"
    />
  </SliderRoot>
</template>
